/**
 * Public branding for the request's resolved tenant.
 *
 * Drives the FE Header on tenant subdomains so the visitor sees the
 * tenant's logo + name instead of the generic Forgmatic wordmark.
 * Same shape as ``GET /public/orgs/{slug}/branding`` but resolves
 * the slug via TenantContextMiddleware so the FE doesn't need to
 * parse its own hostname or carry the slug around. PII fields
 * (ICO/DIC/bank/email) and the custom_domain mapping are
 * intentionally omitted.
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import type { WeeklySchedule } from "@/api/queries/admin/machines";
import { apiFetch } from "@/lib/api";
import { DEFAULT_LANG } from "@/i18n";

const API = "/api/v1";

/**
 * Per-tenant facts driving the landing-page highlight rows.
 *
 * Mirror of :class:`app.services.landing_facts.LandingFacts`. Each
 * field is "honest about reality" — ``null`` / ``0`` / empty list
 * means the tenant hasn't configured that dimension, and the FE
 * skips the corresponding highlight item rather than rendering
 * "0 materials".
 */
export interface DeliveryTierFact {
  code: string;
  /**
   * The tenant's own name for this speed ("Standardní", "Expres") — the
   * same string checkout shows. Surfaces that name a speed take it from
   * here; a fixed word would have the shop advertise a tier it never
   * configured (KAN-705).
   */
  label_cz: string;
  /** English override; `null` ⇒ fall back to `label_cz`. */
  label_en: string | null;
  /** Název per jazyk platformy (KAN-1804); neprázdný blob vyhrává nad ``label_cz`` / ``label_en``. */
  label_i18n?: Record<string, string>;
  /** Production lead time of the tier — NOT what the landing promises. */
  min_days: number;
  day_basis: "calendar" | "business";
  /**
   * Earliest working-day delivery: ``min_days`` + the fastest transit
   * checkout offers (carrier + country + rate band). What the landing words
   * as the promise; `null` ⇒ transit unknown, so no number is stated
   * (KAN-1803).
   */
  delivery_days?: number | null;
}

export interface LandingFacts {
  service_capabilities: string[];
  material_count: number;
  material_names: string[];
  /**
   * Generic per-axis min/max across priced variants, keyed by dimension
   * key. Label/unit come from the declaring process's dimension row (for a
   * material-property dimension, the unit is the property's) — the FE
   * names no axis.
   */
  dimension_ranges: Record<
    string,
    {
      min: number;
      max: number;
      label_i18n: Record<string, string>;
      unit: string | null;
    }
  >;
  /**
   * Fastest dispatch the tenant offers, whatever that tier is called —
   * NOT a claim that an express tier exists. Name the speed from
   * `delivery_tiers[0].label_cz` instead (KAN-705).
   */
  express_dispatch_days: number | null;
  payment_card: boolean;
  shipping_country_codes: string[];
  shipping_carrier_names: string[];
  delivery_tiers: DeliveryTierFact[];
  accepted_file_formats: string[];
  /**
   * Max upload size in MB across public-entry modules. ``null`` when
   * no public-entry module accepts files. ``UploadDropzone`` consumes
   * this as ``maxBytes`` so 3D-only tenants get the 3D module's 50 MB
   * cap instead of the laser-default 20 MB.
   */
  max_upload_mb: number | null;
  /**
   * Tightest "Analýza < N s" SLA across public-entry file-driven
   * modules. ``null`` hides the speed pill entirely (no module
   * declares a calc-time promise — chain-only finishes, manual
   * consult-only flows, etc.).
   */
  sla_calc_seconds: number | null;
  /**
   * Path to a static demo file under ``apps/web/public/`` taken from
   * the primary public-entry module. ``null`` suppresses the "try
   * sample file" CTA on the landing dropzone.
   */
  sample_file_url: string | null;
  /**
   * Slug of the primary public-entry process (lowest
   * ``landing_order`` with ``requires_file=true``). Drives the
   * file-drop smart-router so a non-laser tenant's drop routes to
   * the right module's landing instead of the legacy laser pipeline.
   */
  primary_public_entry_slug: string | null;
  data_residency_eu: boolean;
  /**
   * Order subtotal floor (CZK) configured in admin Order Limits.
   * Drives the "od X Kč" badge on public process landings and the
   * /pricing "Minimum order" stat. ``null`` = no floor configured,
   * FE skips the badge rather than promising a number that doesn't
   * match checkout.
   */
  min_subtotal_czk: number | null;
  /**
   * KAN-1669 — the top-up minimum: a smaller order is topped up to it, not
   * blocked. Same setting the cart's "Dorovnání minimální objednávky" row
   * reads, in minor units of the tenant's books currency. ``null`` = no
   * minimum, the landing says nothing about one.
   */
  minimum_order: MinimumOrderFact | null;
  /**
   * KAN-1903 — the shop's per-line quantity cap (admin Order Limits), the
   * same setting the cart enforces. ``null`` = no cap. Optional because an
   * older API build does not send it.
   */
  max_qty_per_line?: number | null;
  /** KAN-2116 — jak vypadá číslo objednávky tohoto obchodu (řada „order"). */
  order_number_example?: string | null;
}

/** Mirror of :class:`app.services.landing_facts.MinimumOrderFact`. */
export interface MinimumOrderFact {
  value_minor: number;
  currency_code: string;
}

export interface PublicShopReadiness {
  checkout_ready: boolean;
  shipping_ready: boolean;
  delivery_ready: boolean;
  card_payments_ready: boolean;
  currency_ready: boolean;
  enabled_country_count: number;
  active_carrier_count: number;
  warning_codes: string[];
}

export interface PublicMachineProof {
  name: string;
  process_type: string;
  description: string | null;
}

export interface PublicReviewProof {
  rating: number;
  comment: string;
}

export interface PublicOrgProof {
  machine_count: number;
  machines: PublicMachineProof[];
  review_count: number;
  average_rating: number | null;
  reviews: PublicReviewProof[];
}

export interface PublicBusinessInfo {
  legal_name: string;
  street: string;
  city: string;
  postal_code: string;
  country_code: string;
  ico: string;
  dic: string | null;
  support_email: string;
  phone: string | null;
  business_hours: string | null;
  pickup_address: string | null;
  pickup_hours: string | null;
  /** Hodiny osobního odběru jako týdenní rozvrh; přednost před textem. */
  pickup_schedule?: WeeklySchedule | null;
  vat_payer: boolean;
  currency: string;
  /**
   * Adresa deklarovaná pro poptávky (KAN-368) — složená za běhu
   * z ověřené poštovní domény tenanta. `null` = nedeklarovaná nebo
   * doména není ověřená; **není** to „poptávky zavřené", Contact
   * Inbox je catch-all celé domény tak jako tak.
   *
   * Vedle `support_email`, ne místo něj: `support_email` je volný text
   * z Business Info a může mířit kamkoli.
   */
  inquiry_email?: string | null;
}

/**
 * Numbers the platform MEASURES about itself, as opposed to ones it
 * is told. Mirror of
 * :class:`app.services.platform_measurements.PlatformMeasurements`.
 *
 * There is no admin write path to these — the platform's typed-number
 * blob (``platform_facts``) was deleted in KAN-1226, and a measurement
 * must not pick up one of its own.
 */
export interface PlatformMeasurements {
  /**
   * Median hours from a shop signing up to the first order it priced.
   * ``null`` when too few shops signed up recently for the median to be
   * anything but an anecdote — render a qualitative claim instead of a
   * number, never a zero.
   */
  median_hours_to_first_order: number | null;
  /** How many shops the median is over. */
  sample_size: number;
}

export interface PublicProcessFaqEntry {
  capability_key: string;
  display_name: string;
  items: Array<{ q: string; a: string }>;
}

/**
 * A platform-enabled UI language. DB-backed replacement for the hardcoded
 * ``SUPPORTED_LANGS``; the FE registers these into ``languageRegistry`` so
 * the ``/:lang`` gate + switcher honour admin-added locales.
 */
export interface PublicLanguage {
  code: string;
  label: string;
  is_default: boolean;
  source: "builtin" | "custom";
}

export interface PublicTenantBranding {
  slug: string;
  name: string;
  logo_url: string | null;
  primary_color: string | null;
  og_image_url: string | null;
  meta_description: string | null;
  /**
   * Per-tenant page visibility map. Missing key (or empty object) =
   * page visible. The FE filters nav links and gates direct URL
   * access on this. Keys are stable identifiers under
   * ``services.site_customization.TOGGLEABLE_PAGES``.
   */
  pages_visibility: Record<string, boolean>;
  /**
   * Storefront theme + landing section variants (KAN-1546/1547). ``theme``
   * null = the original look; ``sections`` lists only what the tenant
   * changed from the theme's own layouts. Catalog: ``shell/siteThemes.ts``.
   */
  site_appearance?: SiteAppearance;
  /**
   * Per-tenant i18n overrides shaped ``{lang: {namespace: {key:
   * value}}}``. Merged on top of the static locale JSON via
   * ``i18next.addResourceBundle`` once the branding query resolves.
   */
  i18n_overrides: Record<string, Record<string, Record<string, string>>>;
  /**
   * Per-tenant facts for landing-page highlight rows. ``null`` on the
   * platform default tenant (forgmatic.com) — the marketing shell has
   * no per-tenant numbers to render.
   */
  landing_facts: LandingFacts | null;
  /**
   * Numbers the platform measured about itself — derived from data, not
   * typed by anyone. Set only on the platform fallback
   * (forgmatic.com); ``null`` on tenant subdomains.
   */
  platform_measurements: PlatformMeasurements | null;
  /**
   * The region the platform's shared cluster physically runs in
   * (``eu-central`` today). Deployment configuration, not something an
   * admin types — so the security page can state it without anybody
   * being able to claim a region the data is not in. ``null`` on tenant
   * subdomains, where the answer is that tenant's own residency and
   * rides in ``landing_facts``.
   */
  platform_region: string | null;
  /**
   * Platform footer claim for the active language, from
   * ``platform_branding.web_tagline_*``. Set only on the platform
   * fallback (forgmatic.com); ``null`` on tenant subdomains, where the
   * Footer renders no tagline at all rather than ours.
   */
  platform_tagline: string | null;
  /**
   * Platform-enabled UI languages (DB-backed). Registered into
   * ``languageRegistry`` on load so the ``/:lang`` gate + switcher honour
   * admin-added locales. Present on every branding response.
   */
  available_languages: PublicLanguage[];
  business_info: PublicBusinessInfo | null;
  process_faq: PublicProcessFaqEntry[];
  checkout_currency: string;
  checkout_fx_rate_to_czk: number;
  shop_readiness: PublicShopReadiness;
  proof: PublicOrgProof;
  /** KAN-35 — ukázkový obchod platformy: „Zaplatit" nezakládá objednávku
   * (vede na ``/demo-shop``) a patička nese „Ukázkový obchod platformy". */
  is_platform_demo?: boolean;
  /** KAN-1908 — obchod nabízí firemní účty (plán s ``b2b``). */
  offers_company_accounts?: boolean;
}

export function useTenantBranding(
  options?: Omit<UseQueryOptions<PublicTenantBranding>, "queryKey" | "queryFn">,
) {
  const { i18n } = useTranslation();
  // ``landing_facts.material_names`` is localized server-side from
  // ``MaterialTranslation`` — the lang travels in the queryKey so
  // React Query doesn't serve a Czech cache after a switch to EN.
  const lang = i18n.language || DEFAULT_LANG;
  return useQuery<PublicTenantBranding>({
    queryKey: ["public", "branding", "current", lang] as const,
    queryFn: () =>
      apiFetch<PublicTenantBranding>(
        `${API}/public/branding?lang=${encodeURIComponent(lang)}`,
      ),
    // Branding rarely changes — keep it cheap.
    staleTime: 5 * 60 * 1000,
    retry: false,
    ...options,
  });
}

/**
 * KAN-1903 — the shop's per-line quantity cap, or ``null`` when none is set
 * (or not loaded yet). One reader for the configurator and the cart, so both
 * bound the quantity field by the number the cart enforces — never a
 * constant of their own.
 */
export function useMaxQtyPerLine(): number | null {
  const branding = useTenantBranding();
  const cap = branding.data?.landing_facts?.max_qty_per_line;
  return typeof cap === "number" && cap > 0 ? cap : null;
}

/** A tenant's storefront look — see ``services.site_customization``. */
export interface SiteAppearance {
  theme: string | null;
  sections: Record<string, string>;
  /** ``{page: {block: false}}`` — absent block = visible (KAN-1635). */
  blocks: Record<string, Record<string, boolean>>;
}
